// Activation model for @raventic/sdk.
//
// The facade decides WHAT runs on a given page. dropdown/modal keep the SDKs'
// selector (click) activation; results and each rce carousel use a URL/document
// predicate; analytics is on-by-default with an optional predicate. The boot-time
// context is built once per boot() and shared by every product.
//
// Iteration 13: results + carousels may also activate LATER than boot. Their
// `activateWhen` accepts, besides a sync predicate (drawn during boot, exactly as
// before), a predicate returning `Promise<boolean>` (drawn once when it resolves
// true) or an ActivationTrigger (built by the helpers in ./triggers.ts — drawn
// once, or on every page event). Each late fire gets a FRESH context whose
// `trigger` carries the fired data (the matched dataLayer entry / element), which
// the id getters receive. The late-draw pipeline lives in ./activation-runtime.ts.
// analytics' `activateWhen` stays sync-only (SyncActivationPredicate).
//
// WHEN predicates run (iterations 20–21): analytics' and results' are called at widget.js
// execution, BEFORE the host document is parsed (the embed is `<script async>`), with the
// SCRIPT-START context, so they must decide on `ctx.url` — results' sync `false` while loading
// gets one re-check at DOMContentLoaded (products/search.ts prepareSearch). Everything the
// widget pipelines evaluate after the DOM wait — that re-check, the carousels' predicates, the
// id getters of boot draws — gets the PAGE context boot() builds at DOM readiness (so a
// `history.replaceState` by a <head> script is reflected), and may read the page.

export interface ActivationContext {
  url: URL;
  document: Document;
  // The data a trigger fired with (a dataLayer entry, a matched element, …);
  // undefined at boot and for a promise predicate. `unknown` on purpose: customers
  // narrow / cast it to what their trigger delivers (e.g. Ga4EventData).
  trigger?: unknown;
}

// results / carousel predicate: sync (decided during boot) or async (boot does NOT
// await it; the widget draws once when it resolves true).
export type ActivationPredicate = (ctx: ActivationContext) => boolean | Promise<boolean>;

// analytics' predicate — sync only (async analytics activation is out of scope).
export type SyncActivationPredicate = (ctx: ActivationContext) => boolean;

// An event source that can activate a widget after boot, once or repeatedly. Built by
// the helpers in ./triggers.ts (onDataLayer / onElement), never by hand. `subscribe`
// starts listening and returns the unsubscribe function; `fire(data)` asks the runtime
// to draw the widget now, with `data` as `ctx.trigger`.
export interface ActivationTrigger {
  readonly kind: "trigger";
  readonly once: boolean;
  subscribe(ctx: ActivationContext, fire: (data?: unknown) => void): () => void;
}

// What results' / a carousel's `activateWhen` accepts (default: true).
export type ActivationCondition = ActivationPredicate | ActivationTrigger;

export function isActivationTrigger(value: unknown): value is ActivationTrigger {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === "trigger" &&
    typeof (value as { subscribe?: unknown }).subscribe === "function"
  );
}

// Build the activation context from the live browser globals. Called by boot() at script
// start (analytics' predicate, results' verdict) and again at DOM readiness (the page context
// the widget pipelines share), and for every late fire with that fire's data as `trigger` (the
// URL is re-read each time).
export function buildContext(trigger?: unknown): ActivationContext {
  const ctx: ActivationContext = {
    url: new URL(window.location.href),
    document,
  };
  if (trigger !== undefined) ctx.trigger = trigger;
  return ctx;
}

// Resolve an optional SYNC predicate against the context, falling back to a
// product-specific default when no predicate was supplied (analytics: always on).
// results/rce go through activation-runtime's startActivation() instead, which also
// understands async predicates and triggers. An async predicate slipped in here
// (e.g. a JS config for analytics) is NOT treated as truthy: it warns and returns
// false, since a pending Promise can't decide a sync activation.
export function resolvePredicate(
  pred: SyncActivationPredicate | undefined,
  ctx: ActivationContext,
  defaultValue: boolean,
): boolean {
  if (!pred) return defaultValue;
  const verdict: unknown = pred(ctx);
  if (
    (typeof verdict === "object" || typeof verdict === "function") &&
    verdict !== null &&
    typeof (verdict as { then?: unknown }).then === "function"
  ) {
    console.warn(
      "[@raventic/sdk] this activateWhen must be synchronous (analytics) — got a Promise; treating it as false",
    );
    return false;
  }
  return Boolean(verdict);
}
